import { Add } from '@mui/icons-material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import { Avatar, Button, ButtonGroup, IconButton, TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { differenceInCalendarDays, isSameDay } from 'date-fns';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import useAppointment from '../../../../../hooks/useAppointment';
import { Booth } from '../../../../../interfaces/interfaces';
import { AppointmentComponentInterface, UiComponentElementInterface } from '../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../../services/helper';
import { AppointmentCreateModal } from '../../../../account/Appointment/AppointmentCreateModal';
import VyTooltip from '../../../../common/VyTooltip';
import { TitleAndDescription } from '../../../pageBuilder/TitleAndDescription';
import AppointmentBoard from '../appointment/AppointmentBoard';

const AppointmentBookingUiComponent = ({ uiComponent }: UiComponentElementInterface<AppointmentComponentInterface>) => {
  const { event, booths } = useSelector(({ common: { event, booths } }) => ({ event, booths }), shallowEqual);
  const templateEditMode = useSelector((state) => state.common.templateEditMode);

  const [currentDate, setCurrentDate] = useState<string>('');
  const [zoom, setZoom] = useState<number>(1);
  const [currentBooth, setCurrentBooth] = useState<Booth>();

  const { t } = useTranslation('appointment');
  const { defaultDate, initializeAppointments } = useAppointment(currentBooth?._id);

  useEffect(() => {
    if (event) {
      const defaultD = defaultDate(event.datetime_start, event.datetime_end);
      defaultD && setCurrentDate(defaultD);
    }
  }, [event]);

  return (
    <>
      <TitleAndDescription component={uiComponent!} />

      <div
        className={`flex flex-col w-full overflow-scroll pb-4 pt-4 max-w-full overflow-x-auto`}
        style={{
          color: uiComponent?.styling?.textColor || '#000',
        }}>
        {booths && event && currentDate !== '' && (
          <>
            <span className="text-2xl font-bold mb-3">{t('appointment:appointment-title')}</span>
            <span className="mb-3">{t('appointment:appointment-description')}</span>
            <div className="flex flex-col justify-center  mt-3">
              {currentDate && differenceInCalendarDays(new Date(event.datetime_end), new Date(event.datetime_start)) > 1 && (
                <DatePicker
                  format="dd.MM.yyyy"
                  label="Date"
                  minDate={new Date(event.datetime_start)}
                  maxDate={new Date(event.datetime_end)}
                  value={new Date(currentDate)}
                  onChange={(e) => {
                    e && setCurrentDate(e.toString());
                  }}
                />
              )}
              <div className="flex w-full justify-end mt-2">
                <ButtonGroup size="small" aria-label="small outlined button group">
                  <Button
                    disabled={zoom <= 1}
                    onClick={() => {
                      setZoom((zoom) => (zoom -= 1));
                    }}>
                    <ZoomOutIcon style={{ fill: uiComponent?.styling?.textColor || '#000' }} />
                  </Button>
                  <Button disabled>
                    <span style={{ color: uiComponent?.styling?.textColor || '#000' }}>x {zoom}</span>
                  </Button>
                  <Button
                    disabled={zoom >= 3}
                    onClick={() => {
                      setZoom((zoom) => (zoom += 1));
                    }}>
                    <ZoomInIcon style={{ fill: uiComponent?.styling?.textColor || '#000' }} />
                  </Button>
                </ButtonGroup>
              </div>
              {booths
                ?.filter((booth) => booth.meetingrooms?.length > 0 && booth.settings.enableAppointments.value === true)
                ?.filter((booth) =>
                  uiComponent?.hideEmptyRooms
                    ? booth.meetingrooms?.some((room) =>
                        room.meetingappointments.some((appointment) => isSameDay(new Date(Date.parse(appointment.datetime_start)), new Date(currentDate)))
                      )
                    : true
                )
                .map((booth) => (
                  <div className="flex mt-4" key={booth._id}>
                    <div className="flex items-center border-r-2">
                      <Avatar src={createDisplayLink(booth.logo, undefined, undefined, undefined, undefined, undefined, true)} />
                      <div style={{ width: '9em' }} className="flex justify-between ml-3 font-bold text-xs break-words items-center">
                        {booth.name}
                        {templateEditMode && (
                          <VyTooltip title={t('appointment:add-appointment-slot')}>
                            <IconButton style={{ background: 'white' }} className="h-6 w-6 mx-2" onClick={() => setCurrentBooth(booth)}>
                              <Add />
                            </IconButton>
                          </VyTooltip>
                        )}
                      </div>
                    </div>
                    <div className="flex-1 pl-2">
                      <AppointmentBoard
                        event={event}
                        date={currentDate}
                        endDate={event.datetime_end}
                        meetingrooms={booth?.meetingrooms?.filter((room) =>
                          uiComponent?.hideEmptyRooms
                            ? room.meetingappointments.some((appointment) => isSameDay(new Date(Date.parse(appointment.datetime_start)), new Date(currentDate)))
                            : true
                        )}
                        bookingmode={!templateEditMode}
                        showDatePicker={false}
                        zoomProp={zoom}
                      />
                    </div>
                  </div>
                ))}
            </div>
            {currentBooth && (
              <AppointmentCreateModal
                booth={{ ...currentBooth, event }}
                onCreate={() => {
                  initializeAppointments(true);
                  setCurrentBooth(undefined);
                }}
                open
                onClose={() => setCurrentBooth(undefined)}
              />
            )}
          </>
        )}
      </div>
    </>
  );
};

export default AppointmentBookingUiComponent;
